import React from "react";
import { useTranslation } from "react-i18next";

import css from "./OrderDetailsDateDish.module.css";
import { ImageReusable } from "../../../../ui";
import { DishType } from "../../../../constants/enums";

const OrderDetailsDateDish = ({ obj, isReplaced, isProteinReplaced, isSauceReplaced, onReplaceClick, onRemoveSubDish, onDishClick }) => {
  const { t, i18n } = useTranslation();
	const {
		meal,
		dish,
		dishId,
		type,
		proteinDish,
		sauceDish,
		excludedIngredients,
	} = obj;

  const currentLanguage = i18n.language;
  const displayMeal =
    meal?.name?.[currentLanguage] || meal?.name?.["en"] || "-";

  const getDishName = (dishObj) =>
    dishObj?.name?.[currentLanguage] || dishObj?.name?.["en"] || "-";

  const visibleDishes = [
    dish,
    ...(type === DishType.BASE ? [proteinDish, sauceDish].filter(Boolean) : []),
  ].filter(Boolean);

  const totals = visibleDishes.reduce(
    (acc, currentDish) => ({
      weight: acc.weight + (Number(currentDish?.weight) || 0),
      weightNetto: acc.weightNetto + (Number(currentDish?.weightNetto) || 0),
      energy: acc.energy + (Number(currentDish?.energy) || 0),
      proteins: acc.proteins + (Number(currentDish?.proteins) || 0),
      fats: acc.fats + (Number(currentDish?.fats) || 0),
      carbs:
        acc.carbs +
        (Number(currentDish?.carbohydrates) || Number(currentDish?.carbs) || 0),
      cost: acc.cost + (Number(currentDish?.cost) || 0),
    }),
    {
      weight: 0,
      weightNetto: 0,
      energy: 0,
      proteins: 0,
      fats: 0,
      carbs: 0,
      cost: 0,
    },
  );

  const getDishNutrition = (dishObj) => ({
    weight: Math.round(Number(dishObj?.weight) || 0),
    weightNetto: Math.round(Number(dishObj?.weightNetto) || 0),
    energy: Math.round(Number(dishObj?.energy) || 0),
    proteins: Math.round(Number(dishObj?.proteins) || 0),
    fats: Math.round(Number(dishObj?.fats) || 0),
    carbs: Math.round(
      Number(dishObj?.carbohydrates) || Number(dishObj?.carbs) || 0,
    ),
  });

	const getExcludedText = () => {
		if (!excludedIngredients || excludedIngredients.length === 0) return null;
		return excludedIngredients
			.map((name) =>
				typeof name === "object"
					? name?.[currentLanguage] || name?.en || name?.ru || name?.ge || ""
					: name,
			)
			.filter(Boolean)
			.join(" / ");
	};

	const excludedText = getExcludedText();

	const excludedSquare = excludedText ? (
		<div className={css.excludedSquare}>
			<div className={css.excludedSquareTitle}>⚠ {t("excluded-products")}</div>
			{excludedIngredients.map((name, i) => (
				<div key={i} className={css.excludedSquareItem}>
					{typeof name === "object"
						? name?.[currentLanguage] || name?.en || name?.ru || ""
						: name}
				</div>
			))}
		</div>
	) : null;

  const handleReplaceClick = (type) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    onReplaceClick(type);
  };

  const handleRemoveClick = (kind) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemoveSubDish?.(kind);
  };

  const handleSingleClientInfoDateDish = (dishId) => {
    onDishClick?.(dishId);
  };

  if (type === DishType.BASE) {
    const baseNutrition = getDishNutrition(dish);

    return (
      <div className={css.baseContainer}>
        <div
          className={css.wrap}
          onClick={() => handleSingleClientInfoDateDish(dishId)}
        >
          <div className={`${css.meal} ${css.baseMealCell}`}>
            <span>{displayMeal}</span>
            <span className={css.baseDishBadge}>{t("Base Dish")}</span>
          </div>

          <div className={css.img}>
            <ImageReusable
              src={dish?.img}
              className={css.imageTag}
              alt="Food Image"
            />
          </div>

          <div className={css.name}>
            <div className={css.nameContent}>{getDishName(dish)}</div>
            {isReplaced ? <span className={css.replacedTag}>Replaced</span> : null}
          </div>

          <div className={css.info}>
            <div className={css.weightEnergy}>
              WN: {baseNutrition.weightNetto} g / W: {baseNutrition.weight} g
            </div>
            <div className={css.weightEnergy}>{baseNutrition.energy} kcal</div>
            <div className={css.macronutrients}>
              <span className={css.macroItem}>
                P: {baseNutrition.proteins}g
              </span>
              <span className={css.macroItem}>F: {baseNutrition.fats}g</span>
              <span className={css.macroItem}>C: {baseNutrition.carbs}g</span>
            </div>
            <div className={css.costLine}>
              {t("Cost")}: {Number(dish?.cost || 0).toFixed(2)}
            </div>
          </div>

          <div className={css.btn} onClick={handleReplaceClick("base")}>
            {t("replace")}
          </div>

					{excludedSquare}
        </div>

        <div className={css.subCardsWrap}>
          {proteinDish ? (
            <div
              className={`${css.subCard} ${css.proteinCard}`}
              onClick={() => handleSingleClientInfoDateDish(proteinDish.id)}
            >
              <div className={css.subLabel}>{t("Protein Dish")}</div>
              <div className={css.subCardContent}>
                <div className={css.subImageBlock}>
                  <ImageReusable
                    src={proteinDish?.img}
                    className={css.subImage}
                    alt={getDishName(proteinDish)}
                  />
                </div>
                <div className={css.subInfo}>
                  <div className={css.subNameRow}>
                    <div className={css.subName}>{getDishName(proteinDish)}</div>
                    {isProteinReplaced ? <span className={css.replacedTag}>Replaced</span> : null}
                  </div>
                  <div className={css.nutritionLine}>
                    {(() => {
                      const n = getDishNutrition(proteinDish);
                      return (
                        <>
                          <span>ENERGY: {n.energy} kcal</span>
                          <span>P: {n.proteins} g</span>
                          <span>F: {n.fats} g</span>
                          <span>C: {n.carbs} g</span>
                          <span>W: {n.weight} g</span>
                          <span>W.N: {n.weightNetto} g</span>
                          <span>
                            {t("Cost")}:{" "}
                            {Number(proteinDish?.cost || 0).toFixed(2)}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div className={css.subActions}>
                  <div
                    className={css.subReplaceBtn}
                    onClick={handleReplaceClick("protein")}
                  >
                    {t("replace")}
                  </div>
                  <div
                    className={css.subRemoveBtn}
                    onClick={handleRemoveClick("protein")}
                  >
                    ✕
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`${css.subCard} ${css.proteinCard} ${css.emptySubCard}`}>
              <div className={css.subLabel}>{t("Protein Dish")}</div>
              <div
                className={css.addSubDishBtn}
                onClick={handleReplaceClick("protein")}
              >
                + {t("add")}
              </div>
            </div>
          )}

          {sauceDish ? (
            <div
              className={`${css.subCard} ${css.sauceCard}`}
              onClick={() => handleSingleClientInfoDateDish(sauceDish.id)}
            >
              <div className={css.subLabel}>{t("Sauce Dish")}</div>
              <div className={css.subCardContent}>
                <div className={css.subImageBlock}>
                  <ImageReusable
                    src={sauceDish?.img}
                    className={css.subImage}
                    alt={getDishName(sauceDish)}
                  />
                </div>
                <div className={css.subInfo}>
                  <div className={css.subNameRow}>
                    <div className={css.subName}>{getDishName(sauceDish)}</div>
                    {isSauceReplaced ? <span className={css.replacedTag}>Replaced</span> : null}
                  </div>
                  <div className={css.nutritionLine}>
                    {(() => {
                      const n = getDishNutrition(sauceDish);
                      return (
                        <>
                          <span>ENERGY: {n.energy} kcal</span>
                          <span>P: {n.proteins} g</span>
                          <span>F: {n.fats} g</span>
                          <span>C: {n.carbs} g</span>
                          <span>W: {n.weight} g</span>
                          <span>W.N: {n.weightNetto} g</span>
                          <span>
                            {t("Cost")}:{" "}
                            {Number(sauceDish?.cost || 0).toFixed(2)}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div className={css.subActions}>
                  <div
                    className={css.subReplaceBtn}
                    onClick={handleReplaceClick("sauce")}
                  >
                    {t("replace")}
                  </div>
                  <div
                    className={css.subRemoveBtn}
                    onClick={handleRemoveClick("sauce")}
                  >
                    ✕
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`${css.subCard} ${css.sauceCard} ${css.emptySubCard}`}>
              <div className={css.subLabel}>{t("Sauce Dish")}</div>
              <div
                className={css.addSubDishBtn}
                onClick={handleReplaceClick("sauce")}
              >
                + {t("add")}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={css.wrap} onClick={() => handleSingleClientInfoDateDish(dishId)}>
      <div className={css.meal}>{displayMeal}</div>

      <div className={css.img}>
        <ImageReusable
          src={dish?.img}
          className={css.imageTag}
          alt="Food Image"
        />
      </div>

      <div className={css.name}>
        <div className={css.nameContent}>{getDishName(dish)}</div>
        {isReplaced ? <span className={css.replacedTag}>Replaced</span> : null}
      </div>

      <div className={css.info}>
        <div className={css.weightEnergy}>
          WN: {Math.round(totals.weightNetto)} g / W:{" "}
          {Math.round(totals.weight)} g
        </div>
        <div className={css.weightEnergy}>{Math.round(totals.energy)} kcal</div>
        <div className={css.macronutrients}>
          <span className={css.macroItem}>
            P: {Math.round(totals.proteins)}g
          </span>
          <span className={css.macroItem}>F: {Math.round(totals.fats)}g</span>
          <span className={css.macroItem}>C: {Math.round(totals.carbs)}g</span>
        </div>
        <div className={css.costLine}>
          {t("Cost")}: {totals.cost.toFixed(2)}
        </div>
      </div>
      <div className={css.btn} onClick={handleReplaceClick("base")}>
        {t("replace")}
      </div>

			{excludedSquare}
    </div>
  );
};

export default React.memo(OrderDetailsDateDish);
